import type {
  SATheme,
  SAChatMessage as SAChatMessageType,
  SADisplayGroup,
} from '../../types/stylingAssistant.types';
import { buildDisplayGroups } from '../../hooks/useStylingAssistant';
import SASummaryCard from './SASummaryCard';
import SAFollowUpQuestions from './SAFollowUpQuestions';
import SAProductCarousel from './SAProductCarousel';

interface SAChatMessageProps {
  message: SAChatMessageType;
  theme: SATheme;
  /** ID of the currently active (latest) assistant message */
  activeMessageId: string | null;
  visibleGroupIndexes: number[];
  isProgressivelyDisplaying: boolean;
  groupRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  carouselRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  onFollowUpClick: (question: string) => void;
  onScrollCarousel: (groupIndex: number, direction: 'left' | 'right') => void;
}

export default function SAChatMessage({
  message,
  theme,
  activeMessageId,
  visibleGroupIndexes,
  isProgressivelyDisplaying,
  groupRefs,
  carouselRefs,
  onFollowUpClick,
  onScrollCarousel,
}: SAChatMessageProps) {
  // ─── User Message ──────────────────────────────────────────────────────────
  if (message.type === 'user') {
    return (
      <div className="flex justify-end mb-6">
        <div
          className="px-4 py-3 rounded-2xl max-w-[75%]"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: theme.text,
          }}
        >
          <p className="text-sm font-normal">{message.text}</p>
        </div>
      </div>
    );
  }

  // ─── Assistant Message ─────────────────────────────────────────────────────

  const displayGroups: SADisplayGroup[] = message.productsMap
    ? buildDisplayGroups(message.productsMap)
    : [];

  const hasProducts = displayGroups.length > 0;
  const hasSummary = !!message.summary;
  const hasFollowUps = message.followUpQuestions && message.followUpQuestions.length > 0;

  // Only apply progressive display to the ACTIVE (latest) message
  const isActiveMessage = message.id === activeMessageId;

  return (
    <div className="space-y-5 mb-6">
      {/* Product Groups — displayed FIRST (progressive display for active only) */}
      {hasProducts && (
        <div className="space-y-6 mt-2">
          {displayGroups.map((group, groupIndex) => {
            const isVisible = isActiveMessage
              ? visibleGroupIndexes.includes(groupIndex)
              : true;
            const isNextLoading = isActiveMessage
              ? isProgressivelyDisplaying &&
                !isVisible &&
                (visibleGroupIndexes.length === 0 ||
                  groupIndex <= visibleGroupIndexes[visibleGroupIndexes.length - 1] + 1)
              : false;

            return (
              <SAProductCarousel
                key={`${group.title}-${groupIndex}`}
                theme={theme}
                title={group.title}
                products={group.products}
                groupIndex={groupIndex}
                isVisible={isVisible}
                isNextLoading={isNextLoading}
                groupRef={(el) => {
                  groupRefs.current[groupIndex] = el;
                }}
                carouselRef={(el) => {
                  carouselRefs.current[groupIndex] = el;
                }}
                onScrollLeft={() => onScrollCarousel(groupIndex, 'left')}
                onScrollRight={() => onScrollCarousel(groupIndex, 'right')}
              />
            );
          })}
        </div>
      )}

      {/* Summary Card — only show after all products finish progressive display */}
      {hasSummary && (!isActiveMessage || !isProgressivelyDisplaying) && (
        <SASummaryCard theme={theme} summary={message.summary!} />
      )}

      {/* Follow-up Questions — only show after all products finish progressive display */}
      {hasFollowUps && (!isActiveMessage || !isProgressivelyDisplaying) && (
        <SAFollowUpQuestions
          theme={theme}
          questions={message.followUpQuestions!}
          onQuestionClick={onFollowUpClick}
        />
      )}

      {/* Fallback text when no products and no summary yet (loading state) */}
      {!hasProducts && !hasSummary && message.text && (
        <div
          className="px-4 py-3.5 rounded-2xl"
          style={{
            backgroundColor: 'rgba(238, 68, 35, 0.06)',
            border: '1px solid rgba(238, 68, 35, 0.15)',
          }}
        >
          <p className="text-sm leading-relaxed" style={{ color: theme.text }}>
            {message.text}
          </p>
        </div>
      )}
    </div>
  );
}
